import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { ADMIN_GET_PRODUCTS } from '../graphql/queries';

const LIMIT = 10;

const STATUS_COLORS = {
  active: { bg: 'rgba(16,185,129,0.1)', color: '#059669', label: 'Active' },
  draft: { bg: 'rgba(100,116,139,0.1)', color: '#64748b', label: 'Draft' },
};

function Toast({ message, type, onClose }) {
  return (
    <div style={{
      position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
      background: type === 'error' ? '#f43f5e' : '#10b981',
      color: '#fff', borderRadius: 10, padding: '12px 20px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.2)', fontSize: 14, fontWeight: 500,
      display: 'flex', alignItems: 'center', gap: 12,
    }}>
      {message}
      <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: 0 }}>×</button>
    </div>
  );
}

function ProductRow({ product, onEdit }) {
  const [imgError, setImgError] = useState(false);
  const status = STATUS_COLORS[product.status] || STATUS_COLORS.draft;

  return (
    <tr
      style={{ borderBottom: '1px solid var(--slate-100)' }}
      onMouseEnter={e => (e.currentTarget.style.background = 'var(--slate-50)')}
      onMouseLeave={e => (e.currentTarget.style.background = '')}
    >
      {/* Product */}
      <td style={{ padding: '14px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 52, height: 52, borderRadius: 10, overflow: 'hidden', flexShrink: 0,
            background: 'var(--slate-100)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {product.imageUrls?.[0] && !imgError ? (
              <img
                src={product.imageUrls[0]}
                alt={product.title}
                onError={() => setImgError(true)}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <svg style={{ width: 22, height: 22, color: 'var(--slate-300)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            )}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--slate-800)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 240 }}>
              {product.title}
            </div>
            <div style={{ fontSize: 12, color: 'var(--slate-400)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 240 }}>
              {product.description}
            </div>
          </div>
        </div>
      </td>

      {/* Price */}
      <td style={{ padding: '14px 20px', whiteSpace: 'nowrap' }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--slate-800)' }}>
          ₹{product.sellingPrice.toLocaleString()}
        </div>
        {product.mrp > product.sellingPrice && (
          <div style={{ fontSize: 12, color: 'var(--slate-400)', textDecoration: 'line-through' }}>
            ₹{product.mrp.toLocaleString()}
          </div>
        )}
        {product.discount > 0 && (
          <div style={{ fontSize: 11, color: '#10b981', fontWeight: 600 }}>{product.discount.toFixed(1)}% off</div>
        )}
      </td>

      {/* Variants */}
      <td style={{ padding: '14px 20px' }}>
        {product.variantCount > 0 ? (
          <div>
            <div style={{ fontSize: 13, color: 'var(--slate-700)', fontWeight: 500 }}>
              {product.variantCount} variant{product.variantCount !== 1 ? 's' : ''}
            </div>
            {product.variantAttributeName && (
              <div style={{ fontSize: 11, color: 'var(--slate-400)', marginTop: 2 }}>{product.variantAttributeName}</div>
            )}
          </div>
        ) : (
          <span style={{ fontSize: 13, color: 'var(--slate-300)' }}>—</span>
        )}
      </td>

      {/* Tags */}
      <td style={{ padding: '14px 20px' }}>
        {product.tags?.length > 0 ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, maxWidth: 160 }}>
            {product.tags.slice(0, 2).map(t => (
              <span key={t.id} style={{
                fontSize: 11, background: 'rgba(99,102,241,0.08)', color: 'var(--primary)',
                borderRadius: 6, padding: '3px 8px', fontWeight: 500,
              }}>{t.name}</span>
            ))}
            {product.tags.length > 2 && (
              <span style={{ fontSize: 11, color: 'var(--slate-400)', padding: '3px 0' }}>
                +{product.tags.length - 2}
              </span>
            )}
          </div>
        ) : (
          <span style={{ fontSize: 13, color: 'var(--slate-300)' }}>—</span>
        )}
      </td>

      {/* Status */}
      <td style={{ padding: '14px 20px' }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
          background: status.bg, color: status.color,
        }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: status.color }} />
          {status.label}
        </span>
      </td>

      {/* Edit */}
      <td style={{ padding: '14px 20px', textAlign: 'right' }}>
        <button
          onClick={onEdit}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '7px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
            border: '1.5px solid var(--slate-200)', background: 'var(--white)',
            color: 'var(--slate-600)', cursor: 'pointer', transition: 'all 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.color = 'var(--primary)'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--slate-200)'; e.currentTarget.style.color = 'var(--slate-600)'; }}
        >
          <svg style={{ width: 14, height: 14 }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
          Edit
        </button>
      </td>
    </tr>
  );
}

export default function Products() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(null);
  const [page, setPage] = useState(1);
  const [toast, setToast] = useState(null);
  const debounceRef = useRef(null);

  const { data, loading, error, refetch } = useQuery(ADMIN_GET_PRODUCTS, {
    variables: {
      search: debouncedSearch || undefined,
      page,
      limit: LIMIT,
      status: statusFilter || undefined,
    },
    fetchPolicy: 'network-only',
  });

  const products = data?.adminGetProducts?.products || [];
  const total = data?.adminGetProducts?.total || 0;
  const totalPages = data?.adminGetProducts?.totalPages || 1;

  const handleSearch = (e) => {
    const val = e.target.value;
    setSearch(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(val);
      setPage(1);
    }, 400);
  };

  const handleFilter = (status) => {
    setStatusFilter(status);
    setPage(1);
  };


  return (
    <div>
      <header className="dashboard-header">
        <div>
          <h1 className="dashboard-title">Products</h1>
          <p className="dashboard-subtitle">
            {total} product{total !== 1 ? 's' : ''} in your catalogue
          </p>
        </div>
      </header>

      <main className="dashboard-main">
        {/* Search + Filter */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200, position: 'relative' }}>
            <svg
              style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', width: 17, height: 17, color: 'var(--slate-400)', pointerEvents: 'none' }}
              fill="none" viewBox="0 0 24 24" stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search products..."
              value={search}
              onChange={handleSearch}
              style={{
                width: '100%', paddingLeft: 40, paddingRight: 14, paddingTop: 10, paddingBottom: 10,
                border: '1.5px solid var(--slate-200)', borderRadius: 10, fontSize: 14,
                color: 'var(--slate-700)', background: 'var(--white)', outline: 'none',
                boxSizing: 'border-box', transition: 'border-color 0.15s',
              }}
              onFocus={e => (e.target.style.borderColor = 'var(--primary)')}
              onBlur={e => (e.target.style.borderColor = 'var(--slate-200)')}
            />
          </div>

          <div style={{ display: 'flex', gap: 6 }}>
            {[
              { label: 'All', value: null },
              { label: 'Active', value: 'active' },
              { label: 'Draft', value: 'draft' },
            ].map(opt => {
              const active = statusFilter === opt.value;
              return (
                <button
                  key={opt.label}
                  onClick={() => handleFilter(opt.value)}
                  style={{
                    padding: '9px 18px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                    border: `1.5px solid ${active ? 'var(--primary)' : 'var(--slate-200)'}`,
                    background: active ? 'rgba(99,102,241,0.08)' : 'var(--white)',
                    color: active ? 'var(--primary)' : 'var(--slate-500)',
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Table card */}
        <div style={{ background: 'var(--white)', borderRadius: 14, boxShadow: 'var(--shadow)', overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: '80px 0', textAlign: 'center' }}>
              <div style={{ fontSize: 14, color: 'var(--slate-400)' }}>Loading products...</div>
            </div>
          ) : error ? (
            <div style={{ padding: '80px 0', textAlign: 'center' }}>
              <div style={{ fontSize: 14, color: '#f43f5e', marginBottom: 12 }}>{error.message}</div>
              <button
                onClick={() => refetch()}
                style={{ color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}
              >
                Retry
              </button>
            </div>
          ) : products.length === 0 ? (
            <div style={{ padding: '80px 0', textAlign: 'center' }}>
              <svg style={{ width: 48, height: 48, color: 'var(--slate-300)', margin: '0 auto 16px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
              <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--slate-500)', marginBottom: 4 }}>No products found</div>
              <div style={{ fontSize: 13, color: 'var(--slate-400)' }}>
                {debouncedSearch || statusFilter ? 'Try adjusting your search or filter' : 'Products created in the app will appear here'}
              </div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--slate-100)', background: 'var(--slate-50)' }}>
                    {['Product', 'Price', 'Variants', 'Tags', 'Status', ''].map(col => (
                      <th key={col} style={{
                        padding: '12px 20px', textAlign: 'left', fontSize: 11, fontWeight: 700,
                        color: 'var(--slate-400)', textTransform: 'uppercase', letterSpacing: '0.05em',
                        whiteSpace: 'nowrap',
                      }}>
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {products.map(product => (
                    <ProductRow
                      key={product.id}
                      product={product}
                      onEdit={() => navigate(`/products/${product.id}/edit`)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
            <span style={{ fontSize: 13, color: 'var(--slate-400)' }}>
              Page {page} of {totalPages} &bull; {total} total
            </span>
            <div style={{ display: 'flex', gap: 6 }}>
              {[
                { label: '← Previous', action: () => setPage(p => Math.max(1, p - 1)), disabled: page === 1 },
                { label: 'Next →', action: () => setPage(p => Math.min(totalPages, p + 1)), disabled: page === totalPages },
              ].map(btn => (
                <button
                  key={btn.label}
                  onClick={btn.action}
                  disabled={btn.disabled}
                  style={{
                    padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 500,
                    border: '1.5px solid var(--slate-200)', background: 'var(--white)',
                    color: btn.disabled ? 'var(--slate-300)' : 'var(--slate-600)',
                    cursor: btn.disabled ? 'default' : 'pointer', transition: 'all 0.15s',
                  }}
                >
                  {btn.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </main>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}
